import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Clock, X, ArrowUpRight, Loader2 } from "lucide-react";

interface BillingStatus {
  tier: string;
  subscriptionStatus: string;
  trialDaysLeft: number | null;
  trialEndsAt: string | null;
  stripeConfigured: boolean;
}

export function TrialBanner() {
  const [dismissed, setDismissed] = useState(false);
  const { toast } = useToast();

  const { data: billing } = useQuery<BillingStatus>({
    queryKey: ["/api/billing/status"],
    staleTime: 60_000,
  });

  useEffect(() => {
    const lastDismissed = sessionStorage.getItem("trial_banner_dismissed");
    if (lastDismissed) {
      const dismissedDate = new Date(lastDismissed).toDateString();
      const today = new Date().toDateString();
      if (dismissedDate === today) {
        setDismissed(true);
      }
    }
  }, []);

  const startCheckout = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/billing/checkout", { tier: "growth" });
      return res.json().catch(() => ({}));
    },
    onSuccess: (data) => {
      if (data.url) window.location.href = data.url;
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to start checkout",
        description: err.message || "An unexpected error occurred",
        variant: "destructive",
      });
    },
  });

  if (!billing || billing.subscriptionStatus !== "trialing" || billing.trialDaysLeft === null) {
    return null;
  }

  if (dismissed) return null;

  const daysLeft = billing.trialDaysLeft;
  const isUrgent = daysLeft <= 3;

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem("trial_banner_dismissed", new Date().toISOString());
  };

  return (
    <div
      className={`relative flex items-center justify-between gap-2 sm:gap-3 px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm ${
        isUrgent
          ? "bg-destructive/10 border-b border-destructive/20"
          : "bg-foreground/5 border-b border-border"
      }`}
      data-testid="banner-trial"
    >
      <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
        <Clock className={`h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0 ${isUrgent ? "text-destructive" : "text-muted-foreground"}`} />
        <span className="truncate" data-testid="text-trial-days-left">
          {daysLeft === 0
            ? "Trial expires today"
            : daysLeft === 1
            ? "1 day left on trial"
            : `${daysLeft} days left on trial`}
        </span>
      </div>
      <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
        {billing.stripeConfigured && (
          <Button
            size="sm"
            variant={isUrgent ? "destructive" : "default"}
            className="h-6 sm:h-7 text-[10px] sm:text-xs px-2 sm:px-3 min-h-[44px] sm:min-h-0"
            onClick={() => startCheckout.mutate()}
            disabled={startCheckout.isPending}
            data-testid="button-trial-subscribe"
          >
            {startCheckout.isPending ? (
              <Loader2 className="mr-1 sm:mr-1.5 h-3 w-3 animate-spin" />
            ) : (
              <ArrowUpRight className="mr-1 sm:mr-1.5 h-3 w-3" />
            )}
            <span className="hidden sm:inline">Subscribe now</span>
            <span className="sm:hidden">Subscribe</span>
          </Button>
        )}
        <button
          onClick={handleDismiss}
          className="text-muted-foreground hover:text-foreground transition-colors p-1 min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 sm:p-0.5 flex items-center justify-center"
          data-testid="button-dismiss-trial-banner"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
